import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  CreateAdminData,
  FilterOptions,
  User,
  UserFilters,
  UserResponse,
  UserState,
} from "./types/user-types";
import { ApiError } from "./types/common-types";

const initialState: UserState = {
  users: [],
  user: null,
  selectedUser: null,
  leaderboard: [],
  loading: false,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create User
export const createUser = createAsyncThunk<
  UserResponse,
  Partial<User>,
  { rejectValue: ApiError; state: RootState }
>("users/create", async (userData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/users`, userData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create user" },
    );
  }
});

// ✅ Create Admin
export const createAdmin = createAsyncThunk<
  UserResponse,
  CreateAdminData,
  { rejectValue: ApiError; state: RootState }
>("users/createAdmin", async (adminData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/create-admin`, adminData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create admin" },
    );
  }
});

// ✅ Fetch Users (Paginated)
export const fetchUsers = createAsyncThunk<
  UserResponse,
  UserFilters,
  { rejectValue: ApiError; state: RootState }
>("users/fetch", async (filterParams = {}, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const params = new URLSearchParams();

    Object.entries(filterParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
    const res = await axios.get(`${API_URL}/users?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch users" },
    );
  }
});

// ✅ Fetch Single User by ID
export const fetchUserById = createAsyncThunk<
  User,
  number,
  { rejectValue: ApiError; state: RootState }
>("users/fetchById", async (user_id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/users/${user_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.user;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch user" },
    );
  }
});

// ✅ Delete User
export const deleteUser = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("users/delete", async (user_id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/users/${user_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return user_id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete user" },
    );
  }
});

// ✅ Update User
export const updateUser = createAsyncThunk<
  User,
  { user_id: number; data: Partial<User> },
  { rejectValue: ApiError; state: RootState }
>("users/update", async ({ user_id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.put(`${API_URL}/users/${user_id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.user;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update user" },
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("users/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/users/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

export const fetchLeaderboard = createAsyncThunk<
  { data: User[] },
  string, // 'monthly' | 'allTime'
  { rejectValue: ApiError; state: RootState }
>("users/fetchLeaderboard", async (type, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  try {
    const res = await axios.get(`${API_URL}/leaderboard?type=${type}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch leaderboard" },
    );
  }
});

// ✅ Slice
const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    // resetUserState: (state) => {
    //   state.loading = false;
    //   state.error = null;
    //   state.success = false;
    //   state.user = null;
    // },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.user) {
          state.users.unshift(action.payload.user);
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create user";
      })

      // Create Admin
      .addCase(createAdmin.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.user) {
          // Add the new admin to the beginning of the users array
          state.users.unshift(action.payload.user);
        }
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create admin";
      })

      // Fetch All
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch users";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedUser = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch user";
      })

      // Delete
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.users = state.users.filter(
          (user) => user.user_id !== action.payload,
        );
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete user";
      })

      // Update
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update user in list
        state.users = state.users.map((user) =>
          user.user_id === action.payload.user_id ? action.payload : user,
        );

        // Update selected user if it's the one updated
        if (state.selectedUser?.user_id === action.payload.user_id) {
          state.selectedUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update user";
      })
      .addCase(fetchLeaderboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.loading = false;
        state.leaderboard = action.payload.data;
      })
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch leaderboard";
      });
  },
});

export const { setFilters, clearFilters } = userSlice.actions;
export default userSlice.reducer;
